import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SuiteApiError } from "@weco/suite-client";
import { billingApi } from "./billingApi";
import { goToCheckout } from "./redirect";

const KNOWN_ERRORS = new Set(["not_allowed", "already_paid", "payment_unavailable", "payment_failed"]);

/** The server's error code (`{code}` or drf-standardized `errors[0].code`), if any. */
export function payErrorCode(err: unknown): string | null {
  if (!(err instanceof SuiteApiError)) return null;
  const d = err.details as { code?: unknown; errors?: Array<{ code?: unknown }> } | undefined;
  const code = d?.code ?? d?.errors?.[0]?.code;
  return typeof code === "string" ? code : null;
}

/**
 * "Pay now": open a Paymob checkout for one bill and send the browser there.
 *
 * Nothing is marked paid here. Paymob's signed callback to the server is what
 * settles a bill; the browser only comes back to `/billing/return`.
 */
export function usePayStatement() {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (statementId: string) => billingApi.pay(statementId),
    onSuccess: (data) => {
      goToCheckout(data.checkout_url);
    },
    onError: (err: unknown) => {
      const code = payErrorCode(err);
      toast.error(
        code && KNOWN_ERRORS.has(code)
          ? t(`billing.payError.${code}`)
          : t("billing.payError.generic"),
      );
    },
  });
}
